import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Plus } from "lucide-react";
import { useStationManagementApi } from "../../hooks/useStationManagement";
import CoordinatePicker from "./CoordinatePicker";
import GroupFormModal from "./GroupFormModal";
import { Button, FormField, Overlay, SearchableSelect, SelectField, TextField, Toggle } from "./ui";

const EMPTY = {
  group_id: "",
  location_name: "",
  address_line1: "",
  address_line2: "",
  landmark: "",
  colony: "",
  city: "",
  state: "",
  country: "India",
  zip_code: "",
  latitude: "",
  longitude: "",
  status: "active",
  commenced: false,
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export function validateLocation(form) {
  const e = {};
  if (!form.group_id) e.group_id = "Select a group";
  if (!form.address_line1.trim()) e.address_line1 = "Address line 1 is required";
  if (!form.city.trim()) e.city = "City is required";
  if (!form.state.trim()) e.state = "State is required";
  if (!form.country.trim()) e.country = "Country is required";
  if (form.zip_code.trim() && !/^[A-Za-z0-9 -]{3,20}$/.test(form.zip_code.trim())) e.zip_code = "Enter a valid ZIP code";
  const lat = form.latitude === "" ? null : Number(form.latitude);
  const lng = form.longitude === "" ? null : Number(form.longitude);
  if (lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) e.latitude = "Between -90 and 90";
  if (lng !== null && (Number.isNaN(lng) || lng < -180 || lng > 180)) e.longitude = "Between -180 and 180";
  if ((lat === null) !== (lng === null)) e[lat === null ? "latitude" : "longitude"] = "Enter both latitude and longitude";
  return e;
}

// Create or edit a location.
//  - groups:      list of groups for the dropdown
//  - lockGroup:   group is fixed (used from the station form, where the group is already chosen)
//  - defaultGroupId: preselected group for a new location
//  - onGroupCreated: called when a group is created from inside this form
export default function LocationFormModal({ open, location, groups, groupsLoading, defaultGroupId, lockGroup, onClose, onSaved, onGroupCreated, layer = "z-50" }) {
  const api = useStationManagementApi();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const isEdit = Boolean(location?.location_id);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(location ? fromLocation(location) : { ...EMPTY, group_id: defaultGroupId || "" });
  }, [open, location, defaultGroupId]);

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.group_id, label: `Group ${g.group_id} · ${g.group_name}`, sub: g.owner_name || undefined })),
    [groups]
  );

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (saving) return;
    const found = validateLocation(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])),
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };
      if (isEdit) payload.location_id = location.location_id;
      const saved = await api.saveLocation(payload);
      toast.success(isEdit ? `Location ${saved.location_id} updated` : `Location ${saved.location_id} created`);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not save the location");
    } finally {
      setSaving(false);
    }
  };

  const selectedGroup = groups.find((g) => g.group_id === form.group_id);
  const moveBlocked = isEdit && location.station_count > 0;

  return (
    <>
      <Overlay
        open={open}
        onClose={saving ? undefined : onClose}
        layer={layer}
        title={isEdit ? `Edit location ${location.location_id}` : "Create location"}
        subtitle={isEdit ? location.location_name || location.address_line1 : "The location ID is assigned automatically when you save."}
        width="max-w-2xl"
        footer={
          <>
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={submit}>{isEdit ? "Save changes" : "Create location"}</Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-5" noValidate>
          <FormField label="Group" required error={errors.group_id} hint={moveBlocked ? "This location has stations, so its group can't be changed here." : undefined}>
            {lockGroup ? (
              <div className="flex h-9 items-center rounded-lg border border-neutral-200 bg-neutral-50 px-3 text-13 text-neutral-700">
                {selectedGroup ? `Group ${selectedGroup.group_id} · ${selectedGroup.group_name}` : `Group ${form.group_id}`}
              </div>
            ) : (
              <SearchableSelect
                value={form.group_id}
                onChange={(v) => onChange("group_id", v)}
                options={groupOptions}
                placeholder="Select a group"
                loading={groupsLoading}
                disabled={moveBlocked}
                error={errors.group_id}
                emptyContent="No groups yet"
                footer={
                  onGroupCreated
                    ? (close) => (
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            setGroupModal(true);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-13 font-medium text-neutral-900 hover:bg-neutral-100"
                        >
                          <Plus className="h-4 w-4" /> Create new group
                        </button>
                      )
                    : undefined
                }
              />
            )}
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField className="sm:col-span-2" label="Location name" name="location_name" form={form} errors={errors} onChange={onChange} placeholder="e.g. HGCL Office Hyderabad" maxLength={100} />
            <TextField className="sm:col-span-2" label="Address line 1" name="address_line1" required form={form} errors={errors} onChange={onChange} maxLength={255} />
            <TextField className="sm:col-span-2" label="Address line 2" name="address_line2" form={form} errors={errors} onChange={onChange} maxLength={255} />
            <TextField label="Landmark" name="landmark" form={form} errors={errors} onChange={onChange} maxLength={100} />
            <TextField label="Colony" name="colony" form={form} errors={errors} onChange={onChange} maxLength={100} />
            <TextField label="City" name="city" required form={form} errors={errors} onChange={onChange} maxLength={100} />
            <TextField label="State" name="state" required form={form} errors={errors} onChange={onChange} maxLength={100} />
            <TextField label="Country" name="country" required form={form} errors={errors} onChange={onChange} maxLength={100} />
            <TextField label="ZIP code" name="zip_code" form={form} errors={errors} onChange={onChange} inputMode="numeric" maxLength={20} />
            <div className="sm:col-span-2">
              <CoordinatePicker
                form={form}
                errors={errors}
                onChange={onChange}
                address={[form.location_name, form.address_line1, form.landmark, form.colony, form.city, form.state, form.zip_code, form.country].filter((v) => v && v.trim()).join(", ")}
              />
            </div>
            <SelectField label="Status" name="status" form={form} errors={errors} onChange={onChange} options={STATUS_OPTIONS} />
            <div className="flex items-end">
              <div className="w-full">
                <Toggle label="Commenced" description="Live: listed in the customer app's nearby search" checked={form.commenced} onChange={(v) => onChange("commenced", v)} />
              </div>
            </div>
          </div>
          <button type="submit" className="hidden" />
        </form>
      </Overlay>

      {onGroupCreated && (
        <GroupFormModal
          open={groupModal}
          group={null}
          layer="z-[60]"
          onClose={() => setGroupModal(false)}
          onSaved={(g) => {
            onGroupCreated(g);
            onChange("group_id", g.group_id);
          }}
        />
      )}
    </>
  );
}

function fromLocation(l) {
  return {
    group_id: l.group_id || "",
    location_name: l.location_name || "",
    address_line1: l.address_line1 || "",
    address_line2: l.address_line2 || "",
    landmark: l.landmark || "",
    colony: l.colony || "",
    city: l.city || "",
    state: l.state || "",
    country: l.country || "India",
    zip_code: l.zip_code || "",
    latitude: l.latitude ?? "",
    longitude: l.longitude ?? "",
    status: l.status || "active",
    commenced: Boolean(l.commenced),
  };
}
